import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import styled from 'styled-components';
import PhoneInput from 'react-phone-number-input';
import { isValidPhoneNumber } from 'libphonenumber-js/mobile';
import Select from 'react-select';
import { useQuery } from 'react-query';
import { FormTypes } from '../../../new-lib/constants/common';
import { useContactForm } from '../../contact/hooks/ContactForm';
import { getCompounds } from '../../../new-lib/apis';
import { ListItemType } from '../../contact/components/organic-form/list-item.type';
import CountrySelect from '../../contact/components/organic-form/components/CountrySelect';
import Dropdown from '../../contact/components/organic-form/components/Dropdown';
import NawyNowFormLicenseIcon from '../../../new-components/svg-icons/NawyNowLicenseIcon';
import NawyNowFormEmailIcon from '../../../new-components/svg-icons/NawyNowEmailIcon';
import NawyNowDocumentIcon from '../../../new-components/svg-icons/NawyNowDocumentIcon';
import ImageWrapper from '../../../new-components/ImageWrapper';

interface FormProps {
  location?: {
    areaId?: string;
    developerId?: string;
    compoundId?: string;
    propertyId?: string;
  };
  areas: any[];
  propertyTypes: any[];
  compounds: any[];
}

const NawyNowForm: React.FC<FormProps> = ({
  location,
  areas,
  propertyTypes,
  compounds
}) => {
  const [showModal, setShowModal] = useState(false);
  const eventId = useSelector((state: any) => state.app.eventId);
  const { t } = useTranslation(['contact', 'sell-my-property', 'nawy-now']);
  const { locale } = useRouter();
  const isMobile = useSelector((state: any) => state.app.isMobile);

  useEffect(() => {
    if (sessionStorage.getItem('nawy_now_form') == 'true') {
      setShowModal(true);
    }
  }, []);

  const onFormSubmit = () => {
    setShowModal(true);
    sessionStorage.setItem('nawy_now_form', 'true');
  };

  const { onPhoneNumberChange, formik, customerNumber } = useContactForm({
    onFormSubmit,
    formSource: FormTypes.NAWY_NOW,
    location: location
  });

  const handleListItemSelection = (
    listName: string,
    selectedItem: ListItemType
  ) => {
    if (listName == 'area') {
      formik.setFieldValue('compound', null);
    }
    formik.setFieldValue(listName, selectedItem);
  };

  return (
    <>
      <Container>
        <form onSubmit={formik.handleSubmit} className="form">
          <div className="input-row">
            <div
              className={
                'form-input' +
                (formik.errors.contact_name && formik.touched.contact_name
                  ? ' input-invalid'
                  : '')
              }
            >
              <input
                type="text"
                placeholder={t('name')}
                id="contact_name"
                className="input-field"
                name="contact_name"
                value={formik.values.contact_name}
                onChange={formik.handleChange}
              />
              <div className="form-errors">
                {formik.errors.contact_name && formik.touched.contact_name
                  ? formik.errors.contact_name
                  : null}
              </div>
            </div>
          </div>

          <div className="input-row">
            <div
              className={
                'form-input' +
                (formik.errors.contact_number && formik.touched.contact_number
                  ? ' input-invalid'
                  : '')
              }
            >
              <div
                style={{
                  justifyContent: locale == 'en' ? 'flex-start' : 'flex-end',
                  display: ' flex',
                  flexDirection: 'column',
                  width: '100%',
                  position: 'relative'
                }}
              >
                <PhoneInput
                  value={customerNumber.value}
                  className="phone-input"
                  numberInputProps={{
                    id: 'contact_number',
                    name: 'contact_number',
                    type: 'text',
                    style: {
                      direction: 'ltr',
                      textAlign: locale === 'ar' ? 'right' : 'left',
                      unicodeBidi: 'plaintext'
                    }
                  }}
                  countryCallingCodeEditable={true}
                  defaultCountry={'EG'}
                  countrySelectComponent={props => (
                    <CountrySelect
                      {...props}
                      touched={formik.touched.contact_number}
                      error={formik.errors.contact_number}
                    />
                  )}
                  placeholder={t('phone_number')}
                  focusInputOnCountrySelection
                  onChange={value => {
                    if (value) {
                      onPhoneNumberChange(
                        isValidPhoneNumber((value as string) ?? ''),
                        value
                      );
                      formik.handleChange({
                        target: { name: 'contact_number', value: value }
                      });
                    }
                  }}
                />
              </div>
              <div className="form-errors">
                {formik.errors.contact_number && formik.touched.contact_number
                  ? formik.errors.contact_number
                  : null}
              </div>
            </div>
          </div>

          <div className="input-row">
            <Dropdown className="location-dropdown" locale={locale}>
              <Select
                options={areas}
                name="location"
                placeholder={t('location')}
                instanceId="location-select"
                onChange={(selected: ListItemType) =>
                  handleListItemSelection('area', selected)
                }
                className="select"
                classNamePrefix="select"
                value={formik.values.area?.value ? formik.values.area : null}
              />
              <div className="form-errors">
                {formik.errors.area && formik.touched.area
                  ? formik.errors.area.value
                  : null}
              </div>
            </Dropdown>
          </div>

          <div className="input-row">
            <Dropdown className="compound-dropdown" locale={locale}>
              <Select
                options={compounds}
                name="compound"
                placeholder={
                  formik.values.area.value && compounds?.length == 0
                    ? t('no_available_compounds')
                    : t('compound')
                }
                noOptionsMessage={() => {
                  return t('no_options');
                }}
                instanceId="compound-select"
                onChange={(selected: ListItemType) => {
                  handleListItemSelection('compound', selected);
                }}
                className="select"
                classNamePrefix="select"
                value={
                  formik.values.compound?.value ? formik.values.compound : null
                }
              />
              <div className="form-errors">
                {formik.errors.compound && formik.touched.compound
                  ? formik.errors.compound.value
                  : null}
              </div>
            </Dropdown>
          </div>

          <div className="input-row">
            <Dropdown className="property-type-dropdown" locale={locale}>
              <Select
                options={propertyTypes}
                name="property_type"
                placeholder={t('property_type')}
                instanceId="property-type-select"
                onChange={(selected: ListItemType) =>
                  handleListItemSelection('property_type', selected)
                }
                className="select"
                classNamePrefix="select"
                value={
                  formik.values.property_type?.value
                    ? formik.values.property_type
                    : null
                }
              />
              <div className="form-errors">
                {formik.errors.property_type && formik.touched.property_type
                  ? formik.errors.property_type.value
                  : null}
              </div>
            </Dropdown>
          </div>

          <div className="form-input message-box">
            <textarea
              id="message"
              placeholder={t('note')}
              name="contact_notes"
              className="input-message"
              value={formik.values.contact_notes}
              onChange={formik.handleChange}
              maxLength={60}
            />
          </div>

          <button type="submit" disabled={formik.isSubmitting}>
            {formik.isSubmitting ? (
              <>
                <i className="fas fa-spinner fa-spin" />
                {t('sending')}
              </>
            ) : (
              t('send_your_message')
            )}
          </button>

          {isMobile && (
            <>
              {' '}
              <div className="info-section">
                <NawyNowFormLicenseIcon />
                <div className="info-section-container">
                  <span className="info-section-title">
                    {t('license_number', { ns: 'nawy-now' })}
                  </span>
                  <span className="info-subtitle">
                    {t('license', { ns: 'nawy-now' })}
                  </span>
                </div>
              </div>
              <div className="info-section">
                <NawyNowFormEmailIcon />
                <div className="info-section-container">
                  <span className="info-section-title">{t('email')}</span>
                  <span className="info-subtitle">
                    Customer-support@nawy.com
                  </span>
                </div>
              </div>
              <div className="info-section document">
                <ImageWrapper
                  src={'/assets/images/nawy-now/fra-icon.png'}
                  alt="nawy-now-document"
                  width={24}
                  height={24}
                />{' '}
                <a
                  className="document-btn"
                  href={`${process.env.NEXT_PUBLIC_S3_BUCKET_DOMAIN?.replace(
                    's3.eu-central-1.amazonaws.com',
                    ''
                  )}/prod-images.cooingestate.com/docs/دليل+حماية+المتعاملين+في+القطاع+المالي+غير+المصرفي-1.pdf`}
                  target="_blank"
                >
                  {t('download_fra_document', { ns: 'nawy-now' })}
                </a>
              </div>
            </>
          )}
        </form>
      </Container>
    </>
  );
};

const Container = styled.div`
  width: 50%;
  border-radius: 18px;
  background: #fbfcfe;
  box-shadow: 0px 0px 9px 0px rgba(26, 32, 40, 0.07);
  padding: 40px 22px;
  display: flex;
  align-items: center;
  flex-direction: column;
  /* margin: auto; */
  margin-bottom: 16px;

  .info-section {
    display: flex;
    flex-direction: row;
    align-self: flex-start;
    gap: 8px;
    margin-bottom: 8px;

    &.document {
      margin-top: 16px;
      cursor: pointer;
      margin-inline-start: -4px;
      .document-btn {
        color: #1a2028;
        font-family: materialMedium;
        font-size: 14px;
        text-decoration: underline;
      }
    }

    .info-section-container {
      display: flex;
      flex-direction: column;
      .info-section-title {
        color: #1a2028;
        font-family: materialMedium;
        font-size: 12px;
      }

      .info-subtitle {
        color: #898989;
        font-family: materialLight;
        font-size: 12px;
      }
    }
  }

  .form-header {
    width: 100%;
    display: flex;
    flex-direction: column;
    h4 {
      margin-bottom: 4px;
      color: ${props => props.theme.light.colors.black};
    }
    p {
      margin: 16px 0;
      font-size: ${props => props.theme.font.sizes.s};
      color: ${props => props.theme.light.colors.grey};
    }
  }

  form {
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    font-family: materialRegular;
    gap: 12px;

    .phone-input {
      width: 100%;
      display: flex;
      align-items: center;
      background-color: ${props => props.theme.light.backgroundColor};
      border-radius: ${props => props.theme.borderRadius};
      border: 1px solid #e6e6e6;

      input {
        width: 100%;
        background-color: ${props => props.theme.light.backgroundColor};
        padding: 16px;
        border: none !important;
        border: 1px solid transparent;
        border-radius: ${props => props.theme.borderRadius};
        border-start-start-radius: 0;
        border-end-start-radius: 0;
        border-inline-start: none;

        ::placeholder {
          font-family: materialRegular;
        }

        &:focus {
          outline: none !important;
        }
      }
    }

    .input-row {
      display: flex;
      flex-direction: row;
      gap: 16px;
      width: 100%;
    }

    .form-input {
      display: flex;
      flex-direction: column;
      width: 100%;
      margin-bottom: 8px;
      position: relative;
      label {
        font-size: ${props => props.theme.font.sizes.s};
        font-family: materialMedium;
        color: ${props => props.theme.light.colors.black};
        span {
          color: #ff3a00;
        }
      }
      input {
        width: 100%;
        height: 45px;
        padding: 12px 16px;
        background-color: #ffffff;
        border: 1px solid #e6e6e6;
        border-radius: ${props => props.theme.borderRadius};
        outline: none;
        font-family: materialRegular;
      }
      .form-errors {
        font-size: 12px;
        color: #e70800;
        margin-top: 4px;
        margin-inline-start: 5px;
        font-family: materialRegular;
      }
    }

    .message-box {
      width: 100%;
      height: auto;

      textarea {
        min-height: 110px;
        width: 100%;
        padding: 12px 16px;
        background-color: #ffffff;
        border: 1px solid #e6e6e6;
        border-radius: ${props => props.theme.borderRadius};
        font-size: 14px;
        outline: none;
        font-family: materialRegular;
        resize: none;
      }
    }

    button {
      width: 100%;
      display: flex;
      padding: 8px 0px;
      justify-content: center;
      border-radius: 100px;
      background: #015c9a;
      align-items: center;
      align-self: stretch;
      height: 45px;
      font-size: 16px;
      border: none;
      color: white;
      display: flex;
      flex-direction: row;
      align-items: center;
      justify-content: center;
      gap: 8px;
      font-family: materialRegular;
      cursor: pointer;

      &:hover {
        background-color: ${props => props.theme.light.hoverColors.primaryBlue};
      }
    }
  }
  .headline {
    margin: 8px 0 5px 0;
    h3 {
      font-size: ${props => props.theme.font.sizes.l};
      text-align: center;
    }
  }

  @media screen and (max-width: ${props =>
      props.theme.breakPoints.mobile.max}) {
    width: 100%;
    padding: 16px;

    form {
      gap: 8px;
      .input-row {
        gap: 8px;
        flex-direction: column;
      }

      button {
        max-width: none;
        margin: 24px 0px;
      }
    }
  }
`;

export default NawyNowForm;
